import { BrandMark } from '../lib/brand';
import { startThemePreferences } from '../lib/theme-preferences';
import { HomeHero } from '../features/home-hero';
import '../features/watched.css';
startThemePreferences();
import { useCallback, useEffect, useLayoutEffect, useRef, useState, lazy, Suspense } from 'react';
import { defaultVideo, firstVideo, type MetadataAction, type MetadataDiscover } from '../../../packages/contracts/src/metadata';
import { TitleInformation } from '../features/title-information';
import { LiveSchedule } from '../features/live-guide';
import { isLiveChannel, playbackVideoId } from '../../../packages/contracts/src/live';
import {
  Home,
  Compass,
  Search,
  Library,
  CalendarDays,
  Puzzle,
  Tv,
  MonitorSmartphone,
  CloudDownload,
  Settings,
  Play,
  Plus,
  Film,
  ArrowLeft,
  LoaderCircle,
  RefreshCw,
} from 'lucide-react';
import { AppNav } from '@/features/app-nav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserButton } from '@clerk/react';
import { api, json } from '@/lib/api';
import { useSession } from '@/features/session';
import { InputNavigation } from '../features/input-controls';
import { nativeMode } from '@/lib/native';
const PersonalScreen = lazy(() =>
  import('@/features/personal').then((m) => ({ default: m.PersonalScreen })),
);
const SourcePicker = lazy(() =>
  import('@/features/sources').then((m) => ({ default: m.SourcePicker })),
);
import { ContinueWatching, ContinueWatchingRow, useContinueWatching } from '@/features/continue-watching';
const WatchedDetails = lazy(() => import('@/features/watched').then(m => ({ default: m.WatchedDetails })));
const BrowserPlayer = nativeMode ? null : lazy(() =>
  import('@/features/browser-player').then((m) => ({
    default: m.BrowserPlayer,
  })),
);
import type { LoadCommand } from '@/features/sources';
import { getCatalog, getMeta, genres, safeImage } from '@/lib/catalog';
import type { Meta } from '@streamit/contracts';
import { MediaRow } from '@/features/media-row';
import { useCatalogClient } from '@/features/catalog-client';
const CatalogBrowser = lazy(() => import('@/features/catalog-browser').then(m => ({ default:m.CatalogBrowser })));
import { nextEpisode } from '@streamit/contracts';
import type { SourcePreference } from '@/lib/episode-playback';

const navigation = [
  { id: 'home', name: 'Home', icon: Home },
  { id: 'discover', name: 'Discover', icon: Compass },
  { id: 'search', name: 'Search', icon: Search },
  { id: 'library', name: 'Library', icon: Library },
  { id: 'calendar', name: 'Calendar', icon: CalendarDays },
  { id: 'addons', name: 'Addons', icon: Puzzle },
  { id: 'devices', name: 'Devices', icon: MonitorSmartphone },
  { id: 'downloads', name: 'Cloud downloads', icon: CloudDownload },
  { id: 'settings', name: 'Settings', icon: Settings },
];
function AuthSync() {
  return <UserButton />;
}
export function App({ authConfigured, active = true }: { authConfigured: boolean; active?: boolean }) {
  const session = useSession();
  const [notice, setNotice] = useState('');
  const [sourceVideo, setSourceVideo] = useState<string>();
  const [detailVideo, setDetailVideo] = useState<string>();
  const [sourceTrailers, setSourceTrailers] = useState<'trailer' | 'video-trailer'>();
  const [continuation, setContinuation] = useState<SourcePreference>();
  const [playback, setPlayback] = useState<{ command: LoadCommand; meta: Meta; preference?: SourcePreference; scope: string }>();
  const scope = session.signedIn && session.data ? JSON.stringify([session.data.user.id, session.data.profileId, session.authorizationVersion]) : '';
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const catalogClient = useCatalogClient(session.data?.profileId, scope);
  const invalidateCatalogs = useCallback(() => catalogClient?.invalidateAddons(), [catalogClient]);
  const detailRequest = useRef<AbortController | null>(null);
  const detailEntry = useRef<{ key: string; automatic: boolean } | null>(null);
  const activeDetail = useRef('');
  const detailBackButton = useRef<HTMLButtonElement>(null);
  const detailSourceButton = useRef<HTMLButtonElement>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  useEffect(() => { setPlayback(undefined); setSourceVideo(undefined); setDetailVideo(undefined); setContinuation(undefined); setSelected(null); setDetailsLoading(false); detailRequest.current?.abort(); }, [scope]);
  useEffect(() => () => detailRequest.current?.abort(), []);
  const [route, setRoute] = useState(
    location.hash.slice(1) ||
      (location.pathname === '/pair' ? 'devices' : 'home'),
  );
  // Owned here so Home's hero-first layout can place the row without refetching,
  // and it stays available while catalogs load, fail or are empty.
  const continueState = useContinueWatching(Boolean(session.data) && route === 'home');
  const [heroSlot, setHeroSlot] = useState<HTMLElement | null>(null);
  const resumeRow = <ContinueWatchingRow state={continueState} onResume={(meta, videoId) => { void detail(meta, undefined, videoId); }} />;
  const [query, setQuery] = useState('');
  const [searchSeed, setSearchSeed] = useState<{ query: string; revision: number; scope: string }>();
  const [discoverSeed, setDiscoverSeed] = useState<{ action: MetadataDiscover; revision: number; scope: string }>();
  const searchRevision = useRef(0);
  useEffect(() => { setSearchSeed(undefined); setDiscoverSeed(undefined); }, [scope]);
  const [type, setType] = useState('movie');
  const [genre, setGenre] = useState('All genres');
  const [items, setItems] = useState<Meta[]>([]);
  const [series, setSeries] = useState<Meta[]>([]);
  const [selected, setSelected] = useState<Meta | null>(null);
  // Keep catalogs mounted while a player owns the screen, but stop their motion.
  const catalogVisible = active && !selected && !(BrowserPlayer && playback && playback.scope === scope);
  const detailKey = selected ? JSON.stringify([scope, selected.type, selected.id]) : '';
  activeDetail.current = playback ? '' : detailKey;
  // Place a newly opened title before paint, never again for its metadata update.
  // A profile change cannot reposition a detail admitted under the previous scope.
  useLayoutEffect(() => {
    if (!detailKey || detailEntry.current?.key !== detailKey) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    detailBackButton.current?.focus({ preventScroll: true });
  }, [detailKey]);
  // A result belongs to the screen that produced it. Keeping an old Add
  // confirmation after navigating to/removing from Library is misleading.
  useEffect(() => setNotice(''), [route, selected?.id, selected?.type]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const goToSignIn = () => {
    if (nativeMode) {
      location.hash = 'receiver';
      return;
    }
    const returnTo = `${location.pathname}${location.search}#${route}`;
    location.assign(`/sign-in?return_to=${encodeURIComponent(returnTo)}`);
  };
  const chooseSource = (videoId?: string) => {
    if (detailEntry.current) detailEntry.current.automatic = false;
    setContinuation(undefined);
    if (!session.signedIn) {
      goToSignIn();
      return;
    }
    if (!session.data) {
      location.hash = 'library';
      setSelected(null);
      return;
    }
    if (selected && !detailsLoading) {
      setSourceTrailers(undefined);
      const id = playbackVideoId(selected, videoId ?? detailVideo) || firstVideo(selected);
      if (id) setSourceVideo(id);
      else setNotice('No episode list is available yet. Refresh this title or configure a metadata addon.');
    }
  };
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const change = () => {
      const next = location.hash.slice(1) || 'home';
      if (navigation.some((n) => n.id === next)) {
        detailRequest.current?.abort();
        setSelected(null); setSourceVideo(undefined); setDetailVideo(undefined); setContinuation(undefined);
        setRoute(next);
      }
    };
    addEventListener('hashchange', change);
    return () => removeEventListener('hashchange', change);
  }, []);
  useEffect(() => {
    if (session.data || !['home', 'discover', 'search'].includes(route)) return;
    const controller = new AbortController();
    const timer = setTimeout(
      async () => {
        setBusy(true);
        setError('');
        try {
          const extra: Record<string, string> = {};
          if (route === 'search' && query.trim()) extra.search = query.trim();
          if (genre !== 'All genres') extra.genre = genre;
          const result = await getCatalog(
            type,
            'top',
            extra,
            controller.signal,
          );
          setItems(result.metas || []);
          if (route === 'home') {
            const result = await getCatalog(
              'series',
              'top',
              {},
              controller.signal,
            );
            setSeries(result.metas || []);
          }
        } catch (e) {
          if (!controller.signal.aborted)
            setError(
              e instanceof Error ? e.message : 'Could not load this catalog.',
            );
        } finally {
          if (!controller.signal.aborted) setBusy(false);
        }
      },
      route === 'search' ? 350 : 0,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [route, query, type, genre, retry, !!session.data]);
  const addLibrary = async (meta: Meta) => {
    if (!session.signedIn) {
      goToSignIn();
      return;
    }
    if (!session.data) {
      location.hash = 'library';
      setSelected(null);
      return;
    }
    try {
      await api(`/profiles/${session.data.profileId}/library`, {
        method: 'PUT',
        body: json({ meta }),
      });
      setNotice('Added to your library.');
    } catch (e) {
      setNotice((e as Error).message);
    }
  };
  const go = (id: string) => {
    location.hash = id;
    // Commit route and link seed in the same React update. Waiting for the
    // later hashchange event would remount the old page and issue its requests.
    setRoute(id);
    detailRequest.current?.abort();
    setSelected(null);
    setSourceVideo(undefined);
    setDetailVideo(undefined);
    setContinuation(undefined);
    setSearchSeed(undefined);
    setDiscoverSeed(undefined);
  };
  const detail = async (meta: Meta, addonId?: string, resumeVideo?: string, entry: 'sources' | 'details' = 'sources') => {
    if (activeScope.current !== scope) return;
    detailRequest.current?.abort();
    const abort = new AbortController(); detailRequest.current = abort;
    detailEntry.current = { key: JSON.stringify([scope, meta.type, meta.id]), automatic: false };
    setSelected(meta); setSourceVideo(undefined); setSourceTrailers(undefined); setContinuation(undefined);
    // Calendar opens episode information. Keep its identity separate from the
    // source chooser, which performs stream lookup as soon as it mounts.
    setDetailVideo(entry === 'details' ? resumeVideo : undefined);
    if (meta.type === 'other' && meta.id.startsWith('cloud:')) { setDetailsLoading(false); if (entry === 'sources' && resumeVideo) setSourceVideo(resumeVideo); return; }
    setDetailsLoading(true);
    try {
      const result = catalogClient ? await catalogClient.metadata(meta, abort.signal, addonId)
        : !session.data ? (await getMeta(meta.type, meta.id, abort.signal)).meta : meta;
      if (!abort.signal.aborted && activeScope.current === scope && result?.id === meta.id && result.type === meta.type) {
        setSelected(result);
        if (entry === 'sources' && session.data && !resumeVideo && defaultVideo(result)) {
          detailEntry.current = { key: JSON.stringify([scope, result.type, result.id]), automatic: true };
          setSourceVideo(defaultVideo(result));
        }
      }
    } catch (e) {
      if (!abort.signal.aborted && activeScope.current === scope) setNotice(`Full details could not load. ${(e as Error).message}`);
    } finally {
      if (!abort.signal.aborted && activeScope.current === scope) {
        setDetailsLoading(false);
        if (entry === 'sources' && resumeVideo) setSourceVideo(resumeVideo);
      }
    }
  };
  const title = navigation.find((n) => n.id === route)?.name || 'Home';
  const navigateMetadata = (action: Exclude<MetadataAction, { kind: 'external' }>) => {
    if (action.kind === 'search') {
      go('search'); setQuery(action.query); setSearchSeed({ query: action.query, revision: ++searchRevision.current, scope });
    } else if (action.kind === 'discover') {
      go('discover'); setDiscoverSeed({ action, revision: ++searchRevision.current, scope });
    } else void detail({ id: action.id, type: action.type, name: action.id }, undefined, action.videoId);
  };
  const next = playback && playback.scope === scope ? nextEpisode(playback.meta, playback.command.videoId) : undefined;
  const nextPlayback = playback && next?.status === 'ready' ? {
    id: next.video.id,
    title: `S${next.video.season} E${next.video.episode} · ${next.video.title || 'Next episode'}`,
    autoplay: session.data?.profiles.find(p => p.id === session.data?.profileId)?.settings.autoplay === true,
    autoplayDelaySeconds: session.data?.profiles.find(p => p.id === session.data?.profileId)?.settings.autoplayDelaySeconds,
    nextEpisodeReminderSeconds: session.data?.profiles.find(p => p.id === session.data?.profileId)?.settings.nextEpisodeReminderSeconds,
    onSelect: () => {
      if (!scope || activeScope.current !== playback.scope) return;
      const verified = nextEpisode(playback.meta, playback.command.videoId);
      if (verified.status !== 'ready' || verified.video.id !== next.video.id) return;
      setSelected(playback.meta);
      setContinuation(playback.preference);
      setSourceVideo(verified.video.id);
      setPlayback(undefined);
    },
  } : undefined;
  return (
    <div className="app-shell">
      {!nativeMode && <InputNavigation/>}
      <AppNav
        items={navigation.filter((n) => !nativeMode || !['devices', 'downloads'].includes(n.id))}
        route={route}
        go={go}
        native={nativeMode}
        onAccount={() => goToSignIn()}
        account={!nativeMode && authConfigured ? <AuthSync /> : nativeMode || session.signedIn ? <span className="avatar" aria-hidden="true">S</span> : null}
        signIn={!session.signedIn ? <Button className="signin-button" onClick={() => goToSignIn()}>Sign in</Button> : undefined}
      />
      <main id="main-content" tabIndex={-1} className="app-main" data-route={selected ? 'details' : route}>
        {notice && (
          <div className="app-notice" role="status">
            {notice}
            <button
              onClick={() => setNotice('')}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        )}
        {session.data && ['home','discover','search'].includes(route) && <div hidden={!!selected} className={route === 'home' ? 'home-stack' : undefined}>
          {/* Home order: hero (portalled by CatalogBrowser into this slot), then one stable
              Continue Watching instance that never remounts as catalogues load, fail or arrive. */}
          {route === 'home' && <div ref={setHeroSlot} className="home-hero-slot" />}
          {route === 'home' && <div className="page-content home-continue">{resumeRow}</div>}
          <Suspense fallback={<p role="status">Loading catalogs…</p>}>
            {catalogClient ? <CatalogBrowser heroSlot={route === 'home' ? heroSlot : undefined} key={`${scope}:${route}:${searchSeed?.scope === scope ? searchSeed.revision : 0}:${discoverSeed?.scope === scope ? discoverSeed.revision : 0}`} client={catalogClient} visible={catalogVisible} mode={route as 'home'|'discover'|'search'} searchQuery={searchSeed?.scope === scope ? searchSeed.query : undefined} catalogLink={route==='discover' && discoverSeed?.scope === scope ? discoverSeed.action : undefined} onSelect={(meta, addonId) => { void detail(meta, addonId); }}/>
              : <p role="status">Loading your catalogs…</p>}
          </Suspense>
        </div>}
        {selected ? (
          <section className="details">
            <button ref={detailBackButton} data-input-back className="text-button" onClick={() => { detailRequest.current?.abort(); setSelected(null); }}>
              <ArrowLeft size={18} />
              Back
            </button>
            <div
              className={`detail-hero${safeImage(selected.background) ? '' : ' detail-hero-missing-art'}`}
              style={{
                backgroundImage: safeImage(selected.background)
                  ? `url("${safeImage(selected.background)}")`
                  : undefined,
              }}
            >
              <div className="hero-shade" />
              <div className="hero-copy">
                <p className="eyebrow">
                  {selected.type === 'series' ? 'Series' : selected.type === 'movie' ? 'Film' : selected.type === 'tv' ? 'Live channel' : selected.type}
                </p>
                {safeImage(selected.logo) && <img className="title-logo" src={safeImage(selected.logo)} alt="" referrerPolicy="no-referrer"/>}
                <h1>{selected.name}</h1>
                <p className="metadata">
                  {selected.releaseInfo}{' '}
                  {selected.runtime && ` · ${selected.runtime}`}{' '}
                  {selected.imdbRating && ` · ★ ${selected.imdbRating}`}
                </p>
                <div className="hero-actions">
                  <Button ref={detailSourceButton} disabled={detailsLoading} onClick={() => chooseSource()}>
                    <Play fill="currentColor" size={18} />
                    Choose a source
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void addLibrary(selected)}
                  >
                    <Plus size={19} />
                    My library
                  </Button>
                </div>
                <p className="description">{selected.description}</p>
              </div>
            </div>
            <TitleInformation key={detailKey} meta={selected} onNavigate={navigateMetadata} onTrailers={session.data && !detailsLoading ? () => { setSourceTrailers('trailer'); setContinuation(undefined); setSourceVideo(selected.id); } : undefined}/>
            <Suspense fallback={<p role="status">Loading watched status…</p>}>
              {detailsLoading && <p role="status">Loading title details…</p>}
              {!detailsLoading && (isLiveChannel(selected) ? <LiveSchedule key={`${selected.type}:${selected.id}`} meta={selected}
                active={!sourceVideo} onTrailers={video => { setSourceTrailers('video-trailer'); setContinuation(undefined); setSourceVideo(video); }} onWatch={() => chooseSource(selected.id)} onRefresh={async signal => {
                  if (!signal) return detail(selected);
                  const result = catalogClient ? await catalogClient.metadata(selected, signal, undefined, true)
                    : !session.data ? (await getMeta(selected.type, selected.id, signal)).meta : selected;
                  if (!signal.aborted && activeScope.current === scope && result?.id === selected.id && result.type === selected.type) setSelected(result);
                }}/>
                : <WatchedDetails meta={selected} initialVideoId={detailVideo} selected={sourceTrailers ? undefined : sourceVideo || detailVideo} onPlay={video => chooseSource(video)} onTrailers={video => { setSourceTrailers('video-trailer'); setContinuation(undefined); setSourceVideo(video); }}/>)}
            </Suspense>
          </section>
        ) : session.data && ['home','discover','search'].includes(route) ? null : (
          <>
            {route === 'home' ? <HomeHero choices={[]} visible={catalogVisible} onSelect={meta => void detail(meta)}/> : <section className="page-heading"><h1>{title}</h1></section>}
            <div className="page-content">
              {session.data && route === 'home' && <Suspense fallback={null}>
                <ContinueWatching key={`${session.data.profileId}:${session.authorizationVersion}`} onResume={(meta, videoId) => { void detail(meta, undefined, videoId); }} />
              </Suspense>}
              {['home', 'discover', 'search'].includes(route) ? (
                <>
                  {route === 'search' && (
                    <div className="search-box">
                      <Search />
                      <Input
                        autoFocus
                        aria-label="Search movies and shows"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Movies, shows, and everything in between"
                      />
                    </div>
                  )}
                  {route === 'discover' && (
                    <div className="filters">
                      <label>
                        Content
                        <select
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                        >
                          <option value="movie">Movies</option>
                          <option value="series">Series</option>
                        </select>
                      </label>
                      <label>
                        Genre
                        <select
                          value={genre}
                          onChange={(e) => setGenre(e.target.value)}
                        >
                          {genres.map((g) => (
                            <option key={g}>{g}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  {error ? (
                    <div className="empty-state">
                      <Film size={38} />
                      <h2>Catalog unavailable</h2>
                      <p>{error}</p>
                      <Button
                        variant="outline"
                        onClick={() => setRetry((n) => n + 1)}
                      >
                        <RefreshCw size={16} />
                        Try again
                      </Button>
                      <Button variant="ghost" onClick={() => go('addons')}>
                        Manage addons
                      </Button>
                    </div>
                  ) : busy && !items.length ? (
                    <div className="loading">
                      <LoaderCircle className="spin" />
                      Loading your catalog
                    </div>
                  ) : (
                    <>
                      <MediaRow
                        title={
                          route === 'search'
                            ? 'Search results'
                            : 'Popular movies'
                        }
                        items={items}
                        rail={route === 'home' ? { resetKey: `public:${type}:${genre}` } : undefined}
                        onSelect={detail}
                      />
                      {route === 'home' && (
                        <MediaRow
                          title="Series to settle into"
                          items={series}
                          rail={{ resetKey: 'public:series' }}
                          onSelect={detail}
                        />
                      )}
                    </>
                  )}
                </>
              ) : session.signedIn ? (
                <Suspense
                  fallback={
                    <div className="loading">
                      <LoaderCircle className="spin" />
                      Loading…
                    </div>
                  }
                >
                  <PersonalScreen route={route} onSelect={(meta, addonId, videoId) => { void detail(meta, addonId, videoId, route === 'calendar' ? 'details' : 'sources'); }} onAddonsChanged={invalidateCatalogs} />
                </Suspense>
              ) : (
                <section className="empty-state">
                  <span className="empty-icon">
                    {route === 'devices' ? (
                      <Tv size={40} />
                    ) : route === 'downloads' ? (
                      <CloudDownload size={40} />
                    ) : route === 'addons' ? (
                      <Puzzle size={40} />
                    ) : (
                      <Library size={40} />
                    )}
                  </span>
                  <h2>
                    {route === 'devices'
                      ? 'Your screens, together'
                      : route === 'downloads'
                        ? 'Your cloud library'
                        : route === 'addons'
                          ? 'Make Streamit yours'
                          : 'Keep everything in sync'}
                  </h2>
                  <p>
                    {route === 'devices'
                      ? 'Pair a TV to browse on your phone and play on the big screen.'
                      : route === 'downloads'
                        ? 'Connect TorBox to manage cached files and cloud downloads.'
                        : 'Sign in to manage your personal Streamit space.'}
                  </p>
                  <Button onClick={() => goToSignIn()}>
                    Sign in with Google
                  </Button>
                </section>
              )}
            </div>
          </>
        )}
        <footer className="app-footer">
          <span>streamit.</span>
          <p>Your library. Your screens.</p>
          <a href="/privacy.html">Privacy</a>
          <a href="/terms.html">Terms</a>
          <a href="/about.html">About the pilot</a>
          <a href="/setup">Set up a TV</a>
        </footer>
      </main>
      <Suspense fallback={null}>
        {sourceVideo && selected && session.data && (
          <SourcePicker
            key={`${scope}:${selected.type}:${selected.id}:${sourceVideo}:${sourceTrailers}`}
            meta={selected}
            initialVideoId={sourceVideo}
            trailers={sourceTrailers}
            continuation={continuation}
            finalFocus={detailEntry.current?.key === detailKey && detailEntry.current.automatic && !sourceTrailers ? () => {
              // The catalog card that opened this chooser has been unmounted.
              // Do not reclaim focus after navigation, playback or another user focus.
              const target = detailSourceButton.current;
              const focused = document.activeElement;
              if (activeScope.current !== scope || activeDetail.current !== detailKey ||
                  !target?.isConnected || target.disabled ||
                  (focused && focused !== document.body && focused !== target && !focused.closest('.source-dialog')))
                return false;
              return target;
            } : undefined}
            onClose={() => { setSourceVideo(undefined); setSourceTrailers(undefined); setContinuation(undefined); }}
            onPlay={(command, preference) => {
              if (scope && activeScope.current === scope) setPlayback({ command, meta: sourceTrailers ? { id: command.mediaId, type: command.type, name: command.title } : selected, preference: sourceTrailers ? undefined : preference, scope });
            }}
          />
        )}
        {BrowserPlayer && playback && session.data && playback.scope === scope && (
          <BrowserPlayer
            key={playback.command.sessionId}
            command={playback.command}
            next={nextPlayback}
            onClose={() => setPlayback(undefined)}
          />
        )}
      </Suspense>
    </div>
  );
}
